import "./Header.scss"
import React, { FC, useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import profileIcon from "../../../assets/images/profile.png"
import ArrowDownIcon from "../../../assets/icons/ArrowDown"
import Menu from "@mui/material/Menu"
import MenuItem from "@mui/material/MenuItem"
import { Typography } from "@mui/material"
import { getUserAsync, handleLoginSuccess, selectRememberMe, selectUserData, setUserData } from "../../../redux/slices/authSlice"
import { LoginResponse, UserData, UsersData } from "../../../types/userTypes"
import { useAppDispatch, useAppSelector } from "../../../redux/hooks"
import { useIsMobile } from "../../../hooks/useIsMobile"
import { updateApiHeaders } from "../../../redux/api/apiConfig"
import { useTranslation } from "react-i18next"
import { HeaderLink, useHeaderLinks } from "../../../config/headerConfig"
import { useTranslationLabel } from "../../../hooks/useTranslationLabel"
import { ROUTES } from "../../../config/routes.const"
import { LANGUAGES } from "../../../types/language.d"
import { useLanguage } from "../../../hooks/useLanguage"
import { useBranding } from "../../../hooks/useBranding"
import { useIsGoTo } from "../../../hooks/useIsGoTo"
import { SideMenu } from "../SideMenu/SideMenu.component"

const Header: FC = () => {
  const location = useLocation()
  const isMobile = useIsMobile()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [users, setUsers] = useState<LoginResponse[] | null>(null)
  const userData: UserData | null = useAppSelector(selectUserData)
  const rememberMe: boolean = useAppSelector(selectRememberMe)
  const { customerToken } = userData?.personal || {}
  const { i18n } = useTranslation()
  const { LOGOUT } = useTranslationLabel()
  const headerLinks = useHeaderLinks()
  const { logo, desktopHeaderLogo, mobileIconeMenu } = useBranding()
  const { name } = useIsGoTo()
  const [selectedLanguage, onLanguageChange] = useLanguage()

  const [isSideMenuOpen, setIsSideMenuOpen] = useState(false)

  useEffect(() => {
    try {
      const users = localStorage.getItem("USERS_UNDER_THIS_USER")
      if (users) {
        const parsedUsers = JSON.parse(users)
        setUsers(parsedUsers)
      }
    } catch (error) {
      console.error("Error parsing users from localStorage:", error)
    }
  }, [customerToken])

  const toggleSideMenu = () => {
    setIsSideMenuOpen(!isSideMenuOpen)
  }

  const handleProfileClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl((prevAnchorEl) => (prevAnchorEl ? null : event.currentTarget))
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleLogout = () => {
    localStorage.clear()
    updateApiHeaders(undefined, undefined)

    dispatch(setUserData(null))
    setAnchorEl(null)
    navigate(ROUTES.LOGIN)
  }

  const renderLanguageSwitcher = () => {
    return (
      <div className="language-switcher">
        {LANGUAGES.map((language) => (
          <div key={language.value} className="flex">
            <span
              className={
                i18n.language === language.value ? "active-language" : ""
              }
              onClick={() => onLanguageChange(language.value)}
            >
              {language.value.toUpperCase()}
            </span>
            {LANGUAGES[LANGUAGES.length - 1].value !== language.value && (
              <span>&#x002F;</span>
            )}
          </div>
        ))}
      </div>
    )
  }

  const renderLinks = () => {
    return headerLinks.map((link: HeaderLink) => {
      return (
        <li key={link.path} className="link">
          <a
            href={link.path}
            className={location.pathname.includes(link.path) ? "active" : ""}
            onClick={(e) => {
              e.preventDefault()
              navigate(link.path)
            }}
          >
            <Typography variant="body1">{link.label}</Typography>
          </a>
        </li>
      )
    })
  }

  const renderMobileHeader = () => {
    return (
      <header className="custom-header-mobile">
        <div className="left-container"></div>
        <img src={logo} className={`App-logo-${name}`} alt="" />
        <img
          src={mobileIconeMenu}
          className="menu-icon"
          alt=""
          onClick={() => toggleSideMenu()}
        />
        <SideMenu
          links={headerLinks}
          isOpen={isSideMenuOpen}
          onClose={toggleSideMenu}
          handleLogout={handleLogout}
          users={users}
          rememberMe={rememberMe}
          dispatch={dispatch}
          navigate={navigate}
          getUserAsync={getUserAsync}
          handleLoginSuccess={handleLoginSuccess}
          routes={ROUTES}
          customerToken={customerToken}
          languages={LANGUAGES}
          onLanguageChange={onLanguageChange}
          i18n={i18n}
        />
      </header>
    )
  }

  const renderDesktopHeader = () => {
    return (
      <header className="custom-header">
        <div className="left-container">
          <img
            src={desktopHeaderLogo}
            className={`App-logo-${name}`}
            alt="logo"
          />
        </div>
        <nav className="nav">
          <ul className="nav-links">{renderLinks()}</ul>
        </nav>
        <div className="right-container">
          {renderLanguageSwitcher()}
          <span onClick={handleProfileClick}>
            <ArrowDownIcon
              className={Boolean(anchorEl) ? "rotate-arrow" : ""}
            />
            <img src={profileIcon} className="profile-icon" alt="profile" />
          </span>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            {users && users.length > 0 && (
              <div className="users-list">
                {users.map((user: LoginResponse, index:number) => (
                  <MenuItem
                    selected={user.customerToken === customerToken}
                    key={index}
                    onClick={() => {
                      handleLoginSuccess(user, rememberMe);
                      dispatch(getUserAsync())
                        .unwrap()
                        .then(() => {
                          navigate(ROUTES.MY_RIDES)
                        })
                    }}
                  >
                    {user.fullMemberName}
                  </MenuItem>
                ))}
              </div>
            )}
            <MenuItem onClick={handleLogout}>{LOGOUT}</MenuItem>
          </Menu>
        </div>
      </header>
    )
  }

  if (!userData) {
    return <header className="custom-header">{renderLanguageSwitcher()}</header>
  }

  return <>{isMobile ? renderMobileHeader() : renderDesktopHeader()}</>
}

export default Header
